(() => {
  if (window.__synergyPdpSilentCartHandler) return;
  window.__synergyPdpSilentCartHandler = true;

  const parser = new DOMParser();

  function isMainPdpAdd(form, submitter) {
    return Boolean(
      form instanceof HTMLFormElement &&
      form.dataset.type === 'add-to-cart-form' &&
      form.closest('product-info[id^="MainProduct-"]') &&
      submitter &&
      submitter.name === 'add'
    );
  }

  function isAccessoryAdd(form) {
    return Boolean(form instanceof HTMLFormElement && form.closest('.synergy-explore-upsells'));
  }

  function updateSectionFromHtml(sectionHtml, elementId) {
    if (!sectionHtml) return false;
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const replacement = doc.getElementById(elementId);
    const current = document.getElementById(elementId);
    if (!replacement || !current) return false;
    current.innerHTML = replacement.innerHTML;
    return true;
  }

  function forceCartCount(count) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    let bubble = icon.querySelector('.cart-count-bubble');
    if (!count || count <= 0) {
      if (bubble) bubble.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      icon.appendChild(bubble);
    }

    let visible = bubble.querySelector('[aria-hidden="true"]');
    if (!visible) {
      visible = document.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      bubble.prepend(visible);
    }
    visible.textContent = count < 100 ? String(count) : '';

    let hidden = bubble.querySelector('.visually-hidden');
    if (!hidden) {
      hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      bubble.appendChild(hidden);
    }
    hidden.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
  }

  async function refreshCartState(addResponse) {
    if (addResponse?.sections) {
      updateSectionFromHtml(addResponse.sections['cart-drawer'], 'CartDrawer');
      updateSectionFromHtml(addResponse.sections['cart-icon-bubble'], 'cart-icon-bubble');
    }

    try {
      const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        forceCartCount(cart.item_count);
      }
    } catch (error) {
      console.warn('Synergy cart count refresh failed', error);
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const inner = drawer.querySelector('.drawer__inner');
      if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
      if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') {
        overlay.onclick = () => drawer.close();
      }
    }
  }

  function setFormError(form, message) {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && text) {
      text.textContent = message || 'Unable to add this item. Please try again.';
      wrapper.removeAttribute('hidden');
    }
  }

  document.addEventListener(
    'submit',
    async (evt) => {
      const form = evt.target;
      const mainPdp = isMainPdpAdd(form, evt.submitter);
      const accessory = isAccessoryAdd(form);
      if (!mainPdp && !accessory) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      const button = evt.submitter || form.querySelector('[type="submit"]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const label = button.querySelector('span');
      const originalLabel = label ? label.textContent : button.textContent;
      const spinner = button.querySelector('.loading__spinner, .loading-spinner');

      const errorWrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      if (errorWrapper) errorWrapper.setAttribute('hidden', '');

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      try {
        const formData = new FormData(form);
        formData.set('sections', 'cart-drawer,cart-icon-bubble');
        formData.set('sections_url', window.location.pathname);

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
          credentials: 'same-origin',
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this item.');
        }

        await refreshCartState(result);

        if (label) {
          label.textContent = 'Added ✓';
        } else {
          button.textContent = 'Added ✓';
        }

        window.setTimeout(() => {
          if (label) label.textContent = originalLabel;
          else button.textContent = originalLabel;
        }, 1400);
      } catch (error) {
        console.error('Synergy add to cart failed', error);
        setFormError(form, error.message);
        if (label) label.textContent = 'Please try again';
        else button.textContent = 'Please try again';
        window.setTimeout(() => {
          if (label) label.textContent = originalLabel;
          else button.textContent = originalLabel;
        }, 1800);
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
      }
    },
    true
  );
})();

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
